/**
 * @module Label
 *
 * Accessible label for form controls. Built on Radix UI Label primitive.
 * Automatically associates with form elements via `htmlFor` prop.
 *
 * @see {@link https://ui.shadcn.com/docs/components/label Shadcn Label}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/label Radix Label}
 *
 * @example
 * // Basic label with input
 * <Label htmlFor="email">Email</Label>
 * <Input id="email" type="email" />
 *
 * @example
 * // With checkbox
 * <div className="flex items-center gap-2">
 *   <Checkbox id="terms" />
 *   <Label htmlFor="terms">Accept terms and conditions</Label>
 * </div>
 */
import { cva, type VariantProps } from "class-variance-authority";
import { Label as LabelPrimitive } from "radix-ui";
import type { ComponentPropsWithoutRef, ElementRef, Ref } from "react";
import { cn } from "../lib/utils";

/** CVA variants for label styling */
const labelVariants = cva("block font-semibold text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70");
interface LabelProps extends ComponentPropsWithoutRef<typeof LabelPrimitive.Root>, VariantProps<typeof labelVariants> {
  ref?: Ref<ElementRef<typeof LabelPrimitive.Root>>;
}

/**
 * Form label with accessible peer state handling.
 * Dims when associated form control is disabled.
 */
const Label = ({
  className,
  ref,
  ...props
}: LabelProps) => <LabelPrimitive.Root className={cn(labelVariants(), className)} ref={ref} {...props} data-sentry-element="LabelPrimitive.Root" data-sentry-component="Label" data-sentry-source-file="label.tsx" />;
export { Label };